import type { ReactNode } from 'react';

const URL_PATTERN = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;
const TRAILING_PUNCTUATION_PATTERN = /[.,!?;:)\]}]+$/;

function normalizeHref(url: string): string {
  return url.toLowerCase().startsWith('www.') ? `https://${url}` : url;
}

function splitTrailingPunctuation(url: string): { linkedText: string; trailingText: string } {
  const trailingText = url.match(TRAILING_PUNCTUATION_PATTERN)?.[0] ?? '';
  if (!trailingText) return { linkedText: url, trailingText: '' };

  return {
    linkedText: url.slice(0, -trailingText.length),
    trailingText,
  };
}

interface LinkifiedTextProps {
  text: string;
}

export default function LinkifiedText({ text }: LinkifiedTextProps) {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(URL_PATTERN)) {
    const rawUrl = match[0];
    const index = match.index ?? 0;

    if (index > lastIndex) {
      nodes.push(text.slice(lastIndex, index));
    }

    const { linkedText, trailingText } = splitTrailingPunctuation(rawUrl);
    nodes.push(
      <a
        key={`${index}-${linkedText}`}
        href={normalizeHref(linkedText)}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 hover:opacity-80"
        style={{ color: 'inherit' }}
      >
        {linkedText}
      </a>
    );

    if (trailingText) {
      nodes.push(trailingText);
    }

    lastIndex = index + rawUrl.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return <>{nodes}</>;
}
